import { Users, Award, Coffee, Lightbulb } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { motion } from 'framer-motion';
import { useRef } from 'react';
import { useInView } from 'framer-motion';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const stats = [
  { icon: Users, value: "10+", label: "Fornøyde kunder" },
  { icon: Award, value: "15+", label: "Prosjekter levert" },
  { icon: Coffee, value: "669+", label: "Kaffer drukket" },
  { icon: Lightbulb, value: "∞", label: "Kreative ideer" }
]

export function About() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "200px 0px 0px 0px" });

  const variants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={variants}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
        >
          {/* Content */}
          <div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Vi er <span className="gradient-text">kreative</span> problemløsere
            </h2>
            
            <p className="text-lg text-muted-foreground mb-6">
              Pyksel ble grunnlagt i 2025 med en enkel visjon: å skape visuell kommunikasjon
              som ikke bare ser bra ut, men også leverer resultater. Vi kombinerer nordisk
              designfilosofi med moderne teknologi for å bygge merkevarer som varer.
            </p>
            
            <p className="text-lg text-muted-foreground mb-8">
              Vårt team består av dedikerte designere, utviklere og strateger som brenner
              for å hjelpe bedrifter å realisere sitt fulle potensial gjennom smart design
              og gjennomtenkte løsninger.
            </p>

            {/* Values */}
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="w-2 h-2 rounded-full bg-primary mt-3" />
                <div>
                  <h3 className="font-semibold mb-1">Brukersentrert design</h3>
                  <p className="text-muted-foreground">Alt vi lager har brukeren i sentrum</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-2 h-2 rounded-full bg-primary mt-3" />
                <div>
                  <h3 className="font-semibold mb-1">Bærekraftig tenkning</h3>
                  <p className="text-muted-foreground">Design som varer og tar hensyn til miljøet</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="w-2 h-2 rounded-full bg-primary mt-3" />
                <div>
                  <h3 className="font-semibold mb-1">Innovativ tilnærming</h3>
                  <p className="text-muted-foreground">Vi utforsker alltid nye måter å løse utfordringer på</p>
                </div>
              </div>
              <div className="mt-8">
                  <Link to="/om-oss">
                      <Button size="lg" className="hero-glow smooth-transition hover:scale-105">
                      Les Mer
                      </Button>
                  </Link>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                variants={variants}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.2 }}
              >
                <Card
                  className="glass-card text-center p-8 smooth-transition hover:scale-105 border-0 h-full"
                >
                  <CardContent className="p-0">
                    <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4 mx-auto">
                      <stat.icon className="w-8 h-8 text-primary" />
                    </div>
                    <div className="text-3xl font-bold gradient-text mb-2">{stat.value}</div>
                    <div className="text-muted-foreground">{stat.label}</div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
